import React, { useState } from 'react';
import {
  EdgeProps,
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
  useReactFlow,
} from 'reactflow';
import { X } from 'lucide-react';

const CustomEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  sourceHandleId,
  selected,
}) => {
  const { setEdges } = useReactFlow();
  const [isHovered, setIsHovered] = useState(false);

  const onEdgeDelete = () => {
    setEdges((edges) => edges.filter((edge) => edge.id !== id));
  };
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Determine edge color based on source handle
  const getEdgeColor = () => {
    if (sourceHandleId === 'yes' || sourceHandleId === 'responded' || sourceHandleId === 'variant_a') {
      return '#22c55e'; // green
    }
    if (sourceHandleId === 'no' || sourceHandleId === 'timeout' || sourceHandleId === 'variant_b') {
      return '#3b82f6'; // blue for variant_b
    }
    return '#6366f1'; // default indigo
  };

  const edgeColor = selected ? '#f97316' : getEdgeColor();

  // Calculate center point for delete button
  const centerX = (sourceX + targetX) / 2;
  const centerY = (sourceY + targetY) / 2;

  return (
    <g
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          stroke: edgeColor,
          strokeWidth: selected ? 3 : 2,
        }}
      />

      {/* Invisible wider path for easier selection */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        style={{ cursor: 'pointer' }}
      />

      {/* Animated dots along the path */}
      <circle r="3" fill={edgeColor}>
        <animateMotion dur="3s" repeatCount="indefinite" path={edgePath} />
      </circle>

      {/* Edge Label */}
      {sourceHandleId && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
            }}
            className="nodrag nopan"
          >
            <span
              className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                sourceHandleId === 'yes' || sourceHandleId === 'responded' || sourceHandleId === 'variant_a'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                  : sourceHandleId === 'no' || sourceHandleId === 'timeout'
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                  : sourceHandleId === 'variant_b'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
              }`}
            >
              {sourceHandleId === 'yes' ? 'Sim' :
               sourceHandleId === 'no' ? 'Nao' :
               sourceHandleId === 'responded' ? 'Respondeu' :
               sourceHandleId === 'timeout' ? 'Timeout' :
               sourceHandleId === 'variant_a' ? 'Var A' :
               sourceHandleId === 'variant_b' ? 'Var B' : ''}
            </span>
          </div>
        </EdgeLabelRenderer>
      )}

      {/* Delete Button */}
      {(isHovered || selected) && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${centerX}px,${centerY}px)`,
              pointerEvents: 'all',
            }}
            className="nodrag nopan"
          >
            <button
              onClick={onEdgeDelete}
              className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
              title="Remover conexao"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </EdgeLabelRenderer>
      )}
    </g>
  );
};

export default CustomEdge;
